/**
 * NotificationsInbox — Dashboard-Widget (27.08.2026)
 *
 * Zeigt dem eingeloggten Mitarbeiter offene Benachrichtigungen als
 * klickbare Liste:
 *  - Neue Aufgaben-Zuweisungen (mich direkt oder meine Rolle)
 *  - Neue Follow-up-Kommentare in meinen Aufgaben (nicht von mir selbst)
 *
 * Klick öffnet die Aufgabe in ihrem Kontext (globales Board ODER Auftrag,
 * je nach `source`). Danach wird `mark-followups-read` gefeuert, sodass
 * die Zeile aus der Liste verschwindet.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ClipboardList, MessageSquare, Package } from 'lucide-react';
import { authFetch } from '../../lib/auth-fetch';
import { useAuth } from '../../contexts/AuthContext';

const emailKey = (e) => (e || '').replace(/\./g, '__DOT__').replace(/\$/g, '__DLR__');

export default function NotificationsInbox() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [orderBells, setOrderBells] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 27.08.2026: Auch Auftrags-Aufgaben mit einschließen — die sind aus
      // dem globalen Kanban ausgeblendet (`sourceType=order`-Filter), aber
      // für Benachrichtigungen brauchen wir sie unbedingt.
      // 01.09.2026: Zusätzlich rollenbezogene Auftrags-Bells laden
      // (Checklisten-Trigger → z. B. „Kommissionierung starten von Auftrag …").
      const [r1, r2, r3] = await Promise.all([
        authFetch('/api/tasks?mine=false'),
        authFetch('/api/tasks?mine=false&source=order'),
        authFetch('/api/integrations/easybill/orders/notifications/mine'),
      ]);
      const list1 = r1.ok ? await r1.json() : [];
      const list2 = r2.ok ? await r2.json() : [];
      // Dedup by id
      const seen = new Set();
      const merged = [...list1, ...list2].filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id); return true;
      });
      const orderJson = r3.ok ? await r3.json() : {};
      if (!cancelled) {
        setTasks(merged);
        setOrderBells(orderJson.notifications || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const myEmail = (user?.email || '').toLowerCase();
  const myRole = (user?.role || '').toLowerCase();

  const notifications = useMemo(() => {
    const list = [];
    for (const t of tasks) {
      if (t.status === 'done') continue;
      const cursors = t.readCursors || {};
      const cursor = cursors[emailKey(myEmail)];
      const cursorMs = cursor ? new Date(cursor).getTime() : 0;
      const assignees = (t.assignees || []).map(a => (a || '').toLowerCase());
      const roles = (t._assigneeRoles || t.assigneeRoles || []).map(r => (r || '').toLowerCase());
      const isDirectAssignee = assignees.includes(myEmail);
      const isRoleMember = roles.includes(myRole);
      if (!isDirectAssignee && !isRoleMember) continue;
      const createdMs = t.createdAt ? new Date(t.createdAt).getTime() : 0;
      // (1) Neue Aufgabe: kein Cursor gesetzt UND Task wurde nicht von mir angelegt
      if (!cursor && (t.createdBy || '').toLowerCase() !== myEmail) {
        list.push({
          type: 'task', taskId: t.id, source: t.source, sourceId: t.sourceId,
          title: t.title,
          subtitle: t.sourceLabel ? `Aufgabe im Auftrag ${t.sourceLabel}` : 'Neue Aufgabe zugewiesen',
          when: t.createdAt,
        });
      }
      // (2) Neue Follow-ups nach dem Cursor
      for (const f of (t.followups || [])) {
        const fMs = new Date(f.createdAt).getTime();
        if (fMs <= cursorMs) continue;
        if ((f.authorEmail || '').toLowerCase() === myEmail) continue;
        // Auch neu-angelegte Aufgaben (Cursor=0) sollen nicht doppelt gemeldet
        // werden — die Follow-up-Zeile ergänzt die Task-Zeile um Kontext.
        list.push({
          type: 'followup', taskId: t.id, source: t.source, sourceId: t.sourceId,
          title: t.title,
          subtitle: `${f.authorName || f.authorEmail}: ${(f.comment || '').slice(0, 90)}`,
          when: f.createdAt,
        });
      }
    }
    // 01.09.2026: Rollen-basierte Auftrags-Bells aus der Checkliste
    // (z. B. „Kommissionierung starten von Auftrag 11822") einreihen.
    for (const b of orderBells) {
      list.push({
        type: 'order', notifId: b.id, orderId: b.orderId,
        title: b.message || 'Auftrags-Aktion offen',
        subtitle: b.customerLabel
          ? `${b.orderNumber || 'Auftrag'} · ${b.customerLabel}`
          : (b.orderNumber ? `Auftrag ${b.orderNumber}` : 'Neuer Auftrags-Bell'),
        when: b.createdAt,
      });
    }
    return list.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0)).slice(0, 20);
  }, [tasks, orderBells, myEmail, myRole]);

  const openAndAck = async (n) => {
    // 01.09.2026: Order-Bell → Auftrags-Modal (Overview) öffnen und
    // Notification per API als gelesen markieren.
    if (n.type === 'order') {
      setOrderBells(prev => prev.filter(x => x.id !== n.notifId));
      authFetch(`/api/integrations/easybill/orders/notifications/${n.notifId}/read`,
        { method: 'POST' });
      if (n.orderId) {
        nav(`/dashboard/auftraege?order=${encodeURIComponent(n.orderId)}&tab=overview`);
      }
      return;
    }
    // Optimistisch aus der Liste entfernen
    setTasks(prev => prev.map(t => t.id === n.taskId
      ? { ...t, readCursors: { ...(t.readCursors || {}), [emailKey(myEmail)]: new Date().toISOString() } }
      : t));
    // Backend-Cursor setzen (Bell weg auch überall sonst)
    authFetch(`/api/tasks/${n.taskId}/mark-followups-read`, { method: 'POST' });
    // Navigation zum Ziel:
    //  - Auftrags-Aufgabe → OrdersPage mit Deep-Link auf den Tasks-Tab des
    //    passenden Auftrags-Modals (28.02.2026, User-Request „Deep-Link ins
    //    Auftrags-Modal"). Der User landet mit einem Klick genau auf der
    //    Aufgabe im Kontext des Auftrags.
    //  - Globale Aufgabe → wie bisher direkt in den Aufgaben-Dialog.
    if (n.source === 'order' && n.sourceId) {
      nav(`/dashboard/auftraege?order=${encodeURIComponent(n.sourceId)}&tab=tasks`);
    } else {
      nav(`/dashboard/aufgaben?task=${encodeURIComponent(n.taskId)}`);
    }
  };

  if (loading) return null;
  if (notifications.length === 0) return null;

  return (
    <div className="bg-white border border-[#9ec043]/40 rounded-xl p-4 mb-6 shadow-sm" data-testid="notifications-inbox">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#9ec043]" />
          Meine Benachrichtigungen ({notifications.length})
        </h2>
      </div>
      <div className="space-y-1">
        {notifications.map((n, i) => (
          <button
            key={`${n.type}-${n.taskId || n.notifId}-${i}`}
            onClick={() => openAndAck(n)}
            className="w-full text-left flex items-start gap-2 rounded-lg px-3 py-2 hover:bg-[#f8faf2] border border-transparent hover:border-[#9ec043]/30 transition-colors"
            data-testid={`notif-${n.type}-${n.taskId || n.notifId}`}
          >
            <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
              n.type === 'task' ? 'bg-[#9ec043]/15 text-[#7ca735]'
                : n.type === 'order' ? 'bg-orange-100 text-orange-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {n.type === 'task' ? <ClipboardList className="w-3 h-3" />
                : n.type === 'order' ? <Package className="w-3 h-3" />
                : <MessageSquare className="w-3 h-3" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#32373c] font-medium truncate">{n.title}</div>
              <div className="text-[11px] text-gray-500 truncate">{n.subtitle}</div>
            </div>
            <div className="text-[10px] text-gray-400 shrink-0 whitespace-nowrap">
              {n.when ? new Date(n.when).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
